/**
 * B4 (V1, Joey's Gate 3 decision): over-budget and margin warnings on a project.
 *
 * No new financial formulas. Every warning reads a figure the existing
 * project financial layer already produces (docs/project_financial_layer.md):
 * - quotedVsActual = Quoted − Actual Cost   (buildProjectFinancialSummary)
 * - margin         = Final Billed − Actual Cost
 * - actualCost     = computeActualCostFromLedgers (labour + materials + other)
 * - budget_amount  = the project budget the owner entered on the project
 * A warning fires only when one of those existing figures crosses zero or the
 * entered budget. It is reporting only; it never reprices or blocks anything.
 */
import { roundCurrency } from "./projectFinancialReport";

export const BUDGET_WARNING = Object.freeze({
  OVER_BUDGET: "over_budget",
  OVER_QUOTE: "over_quote",
  NEGATIVE_MARGIN: "negative_margin",
});

function finiteOrNull(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * @param {object|null} financial  financialForPanel / loadProjectFinancialSummary result
 * @param {{ budgetAmount?: number|string|null }} [options]
 * @returns {{ id: string, amount: number }[]}  amount = how far past the line, in CAD
 */
export function buildProjectBudgetWarnings(financial, { budgetAmount = null } = {}) {
  if (!financial) return [];
  const warnings = [];
  const actualCost = finiteOrNull(financial.actualCost) ?? 0;

  const budget = finiteOrNull(budgetAmount);
  if (budget !== null && budget > 0 && actualCost > budget) {
    warnings.push({ id: BUDGET_WARNING.OVER_BUDGET, amount: roundCurrency(actualCost - budget) });
  }

  const quotedVsActual = finiteOrNull(financial.quotedVsActual);
  if (financial.hasQuoteSnapshot && Number(financial.quoted) > 0 && quotedVsActual !== null && quotedVsActual < 0) {
    warnings.push({ id: BUDGET_WARNING.OVER_QUOTE, amount: roundCurrency(-quotedVsActual) });
  }

  const margin = finiteOrNull(financial.margin);
  if (Number(financial.billed) > 0 && margin !== null && margin < 0) {
    warnings.push({ id: BUDGET_WARNING.NEGATIVE_MARGIN, amount: roundCurrency(-margin) });
  }

  return warnings;
}
